import React, { useEffect, useState } from "react";
import Loader from "../../../core/common/loader";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Typography,
  useTheme,
} from "@mui/material";
import api from "../../../services/api";
import { showBasicAlert } from "../../../core/common/useAlert";

// Define TypeScript Interfaces for Props
interface FormField {
  id: string;
  label: string;
  type: any;
  options?: string[];
}

interface FormData {
  structure: FormField[];
  responses?: { [key: string]: string };
  id?: number;
}

interface EditLeadProps {
  open: boolean;
  onClose: () => void;
  updateUI: () => void;
  data: FormData[];
}

const EditLead: React.FC<EditLeadProps> = ({
  open,
  onClose,
  updateUI,
  data,
}) => {
  const theme = useTheme();
  const signature = localStorage.getItem("signature");
  const [selectedForm, setSelectedForm] = useState<FormData | null>(null);
  const [responses, setResponses] = useState<{ [key: string]: string }>({});
  const [leadId, setLeadId] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data?.length > 0) {
      setSelectedForm(data[0]);
      setResponses(data[0].responses || {});
      setLeadId(data[0].id || 0);
    }
  }, [data]);

  const handleInputChange = (fieldId: string, value: string) => {
    setResponses({ ...responses, [fieldId]: value });
  };

  const handleMultiSelectChange = (fieldId: string, event: any) => {
    const value = event.target.value;
    setResponses({ ...responses, [fieldId]: value.join(",") });
  };

  const handleUpdate = async () => {
    try {
      setIsLoading(true);
      var dataToSubmit = {
        lead_id: leadId,
        response: responses,
        signature: signature,
      };
      const response = await api.post(
        "leadService/editLeadResponse",
        dataToSubmit
      );
      updateUI();
      showBasicAlert(response.data.message[0].msg);
    } catch (err) {
      showBasicAlert("Error updating lead");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 10,
            backgroundColor: "#f9fafb",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 22, pb: 1 }}>
          📝 Update Lead
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {selectedForm?.structure.map((field) => (
            <Box key={field.id} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.primary"
                gutterBottom
                sx={{ fontWeight: 500 }}
              >
                {field.label} <span className="text-danger">*</span>
              </Typography>

              {field.type === "text" && (
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={responses[field.label] || ""}
                  onChange={(e) =>
                    handleInputChange(field.label, e.target.value)
                  }
                  placeholder={`Enter ${field.label}`}
                />
              )}

              {field.type === "tel" && (
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  type="tel"
                  value={responses[field.label] || ""}
                  onChange={(e) =>
                    handleInputChange(field.label, e.target.value)
                  }
                  placeholder={`Enter ${field.label}`}
                />
              )}

              {field.type === "select" && (
                <Select
                  fullWidth
                  size="small"
                  multiple
                  value={(responses[field.label] || "").split(",")}
                  onChange={(e) => handleMultiSelectChange(field.label, e)}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} color="primary" />
                      ))}
                    </Box>
                  )}
                >
                  {field.options?.map((option, index) => (
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              )}

              {field.type === "date" && (
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  type="date"
                  value={responses[field.label] || ""}
                  onChange={(e) =>
                    handleInputChange(field.label, e.target.value)
                  }
                />
              )}
            </Box>
          ))}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="secondary"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ borderRadius: 2, minWidth: 100 }}
          >
            {isLoading ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditLead;
